import {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * The record a detail screen names in the shell's breadcrumb row (post-release 607e63a).
 *
 * The run, schedule and transformation screens used to draw their own breadcrumb inside the
 * page, under the page header, and the shell's row was missing there (04a-schedule-new.png).
 * The row is the shell's on every screen now; such a page only says what its record is called,
 * by rendering `<PageCrumb label={name} />`, and `breadcrumbTrail` puts the name last. Until the
 * page says it (loading) the trail shows what kind of record it is ("Schedule").
 */
interface PageCrumbValue {
  label: string | null;
  setLabel: (label: string | null) => void;
}

const PageCrumbContext = createContext<PageCrumbValue>({ label: null, setLabel: () => {} });

export function PageCrumbProvider({ children }: { children: ReactNode }) {
  const [label, setLabel] = useState<string | null>(null);
  const value = useMemo(() => ({ label, setLabel }), [label]);
  return <PageCrumbContext.Provider value={value}>{children}</PageCrumbContext.Provider>;
}

/** The record's name the current page has published, or null. */
export function usePageCrumbLabel(): string | null {
  return useContext(PageCrumbContext).label;
}

/**
 * Publishes `label` as the last step of the shell's breadcrumb while mounted. Renders nothing;
 * without the shell (a page rendered on its own) it does nothing.
 */
export function PageCrumb({ label }: { label: string }) {
  const { setLabel } = useContext(PageCrumbContext);
  // A layout effect, so the row never paints the fallback over a name the page already has.
  useLayoutEffect(() => {
    setLabel(label);
    return () => setLabel(null);
  }, [label, setLabel]);
  return null;
}
